import { useState, useEffect, useCallback } from 'react';
import {
  Flex,
  Button,
  Text,
  ResponsiveAcuityInfoPanel,
  theme,
  Icon,
  arrowUp,
  ResponsiveContrastSensitivitySlider,
} from '../../../2022-component-library';
import { useTestContext } from '../../../context/testContext';
import { logmarData } from '../../../helpers/data';
import { useCalibration } from '../../../context/calibrationContext';
import styled from '@emotion/styled';
import { arrowDown } from '../../../2022-component-library';
import { useMediaQuery } from '../../../helpers/useMediaQuery';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { getLogmarLineSize500mm } from '../../../helpers/functions';

const InfoBox = styled(Flex)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fbfbfb;
  font-family: ${(props) => (props.preview ? 'OpticianSans' : null)};
  font-color: ${(props) => props.color};
  border-radius: 4px;
  border: 1px solid ${theme.colors.neutralLight};
  padding: 0.5rem;
  width: ${(props) => props.width || '10vw'};
  min-height: 30px;
`;

const ResponsiveContrastSensitivityTestOptician = ({ eyeTested, goBack, goToResults }) => {
  const {
    calibrationData: { distanceFromScreen, pixelDensity, methodSelected },
    updateCalibrationData,
  } = useCalibration();

  const { testData, setTestFieldValue, generateRandomCharacters, contrastSensitivityData, setContrastSensitivityConfig, updateTestData } =
    useTestContext();

  const [currentLogmar, setCurrentLogmar] = useState(testData?.eyeTested);
  const [testMode, setTestMode] = useState(methodSelected !== 'manual' ? 'auto' : testData.testMode);
  const [contrastValue, setContrastValue] = useState(100);
  const [fontColor, setFontColor] = useState(`hsl(0%, 0%, 100%)`);
  const [currentCharacters, setCurrentCharacters] = useState('');
  const smallScreen = useMediaQuery('(max-width: 700px)');
  const postMessage = usePostMessage();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const messageFromPatient = useReceiveMessage();
  const { patientDistanceReference, pixelDensityReference } = calibrationConstants;

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const handleFinalResultsSubmission = () => {
    const results = testData.contrastSensitivityResults || [];

    if (!results.some((result) => result[eyeTested])) {
      return alert('No results have been saved for this test. Please save at least one set of results to continue.');
    } else goToResults();
  };

  const changeTestMode = useCallback(
    (selectedTestMode) => {
      if (selectedTestMode !== testMode) {
        updateTestData({ testMode: selectedTestMode });
        setTestMode(selectedTestMode);
        postMessage({
          type: 'updateTestMode',
          content: {
            testMode: selectedTestMode,
          },
        });
      }
    },
    [testMode, postMessage],
  );

  const saveTestResults = useCallback(() => {
    const existingResults = testData.contrastSensitivityResults || [];
    setTestFieldValue({
      name: 'contrastSensitivityResults',
      value: [...existingResults, { logmarTested: currentLogmar?.value, [eyeTested]: contrastValue ? 100 / contrastValue : 0 }],
    });

    alert('Your results have been saved.');
  }, [testData?.contrastSensitivityResults, currentLogmar, contrastValue]);

  const updateContrastSensitivityContent = useCallback(
    (contrastSensitivityTestContent) => {
      postMessage({
        type: 'updateContrastSensitivityContent',
        content: contrastSensitivityTestContent,
      });
    },
    [postMessage],
  );

  const handleContrastChange = useCallback((currentValue) => {
    setContrastValue(currentValue);
    setFontColor(`hsl(0, 0%, ${100 - currentValue}%)`);
  });

  const getLogmarFontSize = useCallback(
    (logmar, distance) => {
      let lineSize;
      if (patientDistanceReference === 500) {
        lineSize = getLogmarLineSize500mm(logmar.id);
      }
      const fontSize = (lineSize * distance) / patientDistanceReference / pixelDensity;
      const fontSizeAdjustment = (pixelDensity * testData.fontAdjustmentPercentage) / pixelDensityReference / 100;
      const adjustedFontSize = fontSize + fontSize * fontSizeAdjustment;
      return adjustedFontSize;
    },
    [patientDistanceReference, pixelDensity, testData?.fontAdjustmentPercentage, pixelDensityReference],
  );

  const updateLogmarFontSize = useCallback(
    (logmar, distance) => {
      postMessage({
        type: 'resizeLogmar',
        content: {
          fontSize: Math.round(getLogmarFontSize(logmar, distance)),
          distanceFromScreen: distance,
          logmarCharacters: contrastSensitivityData['settings']['characters'],
          fontColor: fontColor,
        },
      });
      setCurrentLogmar(logmar);
    },
    [contrastSensitivityData?.settings?.characters, fontColor],
  );

  const changeAndSendCharacters = useCallback(() => {
    const newCharacters = generateRandomCharacters().substring(0, 6);
    const newData = {
      characters: newCharacters,
      fontColor: fontColor,
    };
    setCurrentCharacters(newCharacters);
    setContrastSensitivityConfig(newData);
    updateContrastSensitivityContent(newData);
  }, [fontColor]);

  useEffect(() => {
    updateLogmarFontSize(currentLogmar, distanceFromScreen);
  }, [currentLogmar]);

  const changeLogmarValue = useCallback(
    (action) => {
      if (action === 'decrement') {
        setCurrentLogmar(logmarData.find((newLogmar) => newLogmar.id === currentLogmar?.id + 1));
      } else {
        setCurrentLogmar(logmarData.find((newLogmar) => newLogmar.id === currentLogmar?.id - 1));
      }
    },
    [currentLogmar?.id],
  );

  const clearExistingResults = useCallback(() => {
    const existingResults = [...(testData?.contrastSensitivityResults || [])];
    const resultsWithoutCurrentEye = existingResults.filter((result) => {
      return !result.hasOwnProperty(eyeTested);
    });

    const shouldClearAllResults =
      (testData.testLeftEye && !testData.testRightEye) || (testData.testRightEye && !testData.testLeftEye) || testData.testBothEyes;

    updateTestData({ contrastSensitivityResults: shouldClearAllResults ? [] : resultsWithoutCurrentEye });
  }, [eyeTested, testData.testLeftEye, testData.testRightEye, testData.testBothEyes, updateTestData]);

  useEffect(() => {
    if (isInitialLoad === true) {
      postMessage({
        type: 'contrastSensitivity',
        content: {
          fontSize: Math.round(getLogmarFontSize(currentLogmar, distanceFromScreen)),
          distanceFromScreen: distanceFromScreen,
          methodSelected: methodSelected,
          characters: contrastSensitivityData['settings']['characters'],
          fontColor: `hsl(0, 0%, 0%)`,
          testMode: testMode,
        },
      });

      clearExistingResults();
      setIsInitialLoad(false);
      changeAndSendCharacters();
    }
  }, [isInitialLoad, updateTestData]);

  useEffect(() => {
    if (messageFromPatient.type === 'patientMovementDetected') {
      const { patientDistance, patientIsMoving } = messageFromPatient.content;
      updateCalibrationData({ distanceFromScreen: patientDistance });

      if (patientIsMoving === false) {
        updateLogmarFontSize(currentLogmar, patientDistance);
      }
    }
  }, [messageFromPatient]);

  return (
    <Flex column centered maxWidth='700px' paddingBottom='0.5rem'>
      <ResponsiveContrastSensitivitySlider contrastValue={contrastValue} handleContrastChange={handleContrastChange} />
      <Flex justifyContent='space-between' width='90%' margin={smallScreen ? '0.5rem 0 1rem 0' : '2rem 0'}>
        <Flex
          border='1px solid black'
          borderRadius='4px'
          width='45%'
          height='45vh'
          minHeight='160px'
          maxHeight='200px'
          column
          padding='0.5rem'
          alignItems='center'
        >
          <Button width='15em' marginBottom='3rem' backgroundColor={theme.colors.callToAction} onClick={() => changeAndSendCharacters()}>
            Send new characters
          </Button>
          <Flex justifyContent='space-between' width='90%'>
            <Flex column centered>
              <Text fontSize='0.75rem' marginBottom='0.5rem'>
                Patient Preview
              </Text>
              <InfoBox preview color={fontColor}>
                {currentCharacters}
              </InfoBox>
            </Flex>
            <Flex column centered>
              <Text fontSize='0.75rem' marginBottom='0.5rem'>
                Current Characters
              </Text>
              <InfoBox preview>{currentCharacters}</InfoBox>
            </Flex>
          </Flex>
        </Flex>

        <Flex
          border='1px solid black'
          borderRadius='4px'
          padding='0.5rem'
          height='45vh'
          minHeight='160px'
          maxHeight='200px'
          width='45%'
          column
          alignItems='center'
        >
          <Flex width='100%' padding='0 1rem 0.1rem 0.1rem' alignItems='center' justifyContent='space-between'>
            <Text fontSize='0.75rem'>{`LogMAR Size`}</Text>
            <Flex width='10vw' position='relative' alignItems='center'>
              {currentLogmar?.value > -0.2 && (
                <Icon
                  icon={arrowDown}
                  color={theme.colors.primary}
                  marginRight='0.5rem'
                  position='absolute'
                  left='-1rem'
                  onClick={() => changeLogmarValue('decrement')}
                />
              )}
              <InfoBox width='10vw'>
                <Text fontSize='0.75rem'>{currentLogmar?.value?.toFixed(1) ?? '1.0'}</Text>
              </InfoBox>
              {currentLogmar?.value < 1 && currentLogmar?.id > testData.maxPermittedLogmar.id && (
                <Icon
                  icon={arrowUp}
                  color={theme.colors.primary}
                  marginLeft='0.5rem'
                  position='absolute'
                  right='-1rem'
                  onClick={() => changeLogmarValue('increment')}
                />
              )}
            </Flex>
          </Flex>
          <Flex width='100%' padding='0.1rem 1rem 0.1rem 0.1rem' alignItems='center' justifyContent='space-between'>
            <Text fontSize='0.75rem'>Contrast Sensitivity</Text>
            <InfoBox width='10vw'>
              <Text fontSize='0.75rem'>{contrastValue < 0.1 ? '-' : (100 / contrastValue).toFixed(1)}</Text>
            </InfoBox>
          </Flex>
          <Flex width='100%' padding='0.1rem 1rem 0.1rem 0.1rem' alignItems='center' justifyContent='space-between'>
            <Text fontSize='0.75rem'>Contrast %</Text>
            <InfoBox width='10vw'>
              <Text fontSize='0.75rem'>{contrastValue.toFixed(1)}</Text>
            </InfoBox>
          </Flex>
          <Button width='90%' marginTop='0.5rem' onClick={() => saveTestResults()}>
            Save Results
          </Button>
        </Flex>
      </Flex>
      <ResponsiveAcuityInfoPanel changeTestMode={changeTestMode} />
      <Flex marginTop={smallScreen ? '0.25rem' : '2rem'}>
        <Button variant='outlined' marginRight='1rem' onClick={() => goBack()}>
          Back
        </Button>
        <Button onClick={() => handleFinalResultsSubmission()}>Next</Button>
      </Flex>
    </Flex>
  );
};

export default ResponsiveContrastSensitivityTestOptician;
